/**
 * Auto-calibration, editor side (spec 2026-09-05-07 §4).
 *
 * The exact shape of `useDigitizePlan` and for the same reasons: one deliberate
 * click, an asynchronous job, a progress strip, a summary or an honest failure.
 * It shares `useJobPoll` with digitization rather than duplicating the cadence —
 * §1 is one rule, not two.
 *
 * The difference is what it produces: one number with provenance, not a bundle.
 * So re-running is fine, there is no `replace`, and success means the *scene*
 * changed (`mm_per_unit`), not that new geometry appeared.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { $api } from '../../api'
import { useScopeLive } from '../../contexts/LiveEventsContext'
import { apiErrorMessage } from './useDigitizePlan'
import { useElapsedSeconds, useJobPoll } from './useJobPoll'

const CALIBRATE_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/plan/calibrate' as const
const JOBS_PATH = '/api/v1/organizations/{orgCode}/jobs' as const

/**
 * One transcribed candidate, as the job stores it: a printed dimension
 * annotation, or — since spec 2026-09-14-01 — a PMR turning circle reduced to
 * its horizontal diameter, which is why the shape is the same.
 */
export interface CalibrateDimension {
    a: { x: number; y: number }
    b: { x: number; y: number }
    /** Absent or `dimension` for a printed annotation, `pmr_circle` for a circle. */
    kind?: string
    printed: string
    unit?: string
    value_mm: number
    length_px: number
    mm_per_px: number
    rejected?: string
    /**
     * Who decided `length_px` (spec 2026-09-30-02): `model` (its box),
     * `measured` (a circle fitted in the rendered pixels around that box) or
     * `searched` (a circle the sheet search found with no box, spec
     * 2026-10-11-06).
     */
    source?: 'model' | 'measured' | 'searched'
}

/** The `floorplan_calibrate` execution output, as far as the editor reads it. */
export interface CalibrateOutput {
    /** What was written on the frame; 0 on a low-confidence proposal. */
    mm_per_unit?: number
    /**
     * `high` was written on the frame. `low` was not: the editor shows the
     * detected circle and the user accepts it (`POST /plan/calibrate/accept`)
     * or measures by hand (spec 2026-09-30-02 §4).
     */
    confidence?: 'high' | 'low'
    confidence_reason?: string
    /** What accepting a `low` answer writes. */
    proposed_calibration?: { mm_per_unit: number; method: string }
    /**
     * The chosen annotation's endpoints in the **frame's** units — the same
     * shape every other calibration's `measure` carries, so the editor can draw
     * it without converting anything (spec 2026-09-05-07 §4).
     */
    measure_frame?: { a: { x: number; y: number }; b: { x: number; y: number }; distance_mm: number }
    previous_mm_per_unit?: number
    registration_uid?: string
    consensus?: {
        candidates?: CalibrateDimension[]
        survivors?: CalibrateDimension[]
        rejected_cluster?: { count: number; mm_per_px: number; ratio_to_chosen: number }
        mm_per_px?: number
        spread_pct?: number
        measure?: CalibrateDimension
        /** Which tier answered: `dimensions`, `pmr_circle` or `printed_scale`. */
        basis?: string
        /**
         * The cross-check against the scale printed on the sheet (spec
         * 2026-10-16-03), absent when the model read none.
         */
        printed_scale?: {
            denominator?: number
            dpi?: number
            mm_per_px?: number
            readings_mm_per_px?: number
            verdict: 'agrees' | 'disagrees' | 'replaced' | 'answered' | 'unusable'
        }
        failure?: string
        confidence?: 'high' | 'low'
    }
}

export interface CalibrateNotice {
    error: boolean
    message: string
    /** The chosen segment, so the editor can flash it over the plan. */
    measure?: CalibrateDimension
}

export interface CalibratePlan {
    /** False once the endpoint has answered 503 — shared with digitization. */
    available: boolean
    running: boolean
    jobUid: string | null
    elapsedSeconds: number
    notice: CalibrateNotice | null
    enqueueing: boolean
    start: (opts?: { representationUid?: string }) => Promise<void>
    dismiss: () => void
    setNotice: (notice: CalibrateNotice | null) => void
}

interface JobLike {
    uid: string
    final_status?: string | null
    executions?: { output?: unknown; error?: unknown }[]
}

/**
 * @param onFinished called once, after a successful run, with the job output
 * and the job's uid — the page reloads the scene there so the new
 * `mm_per_unit` reaches every readout, or, on a low-confidence proposal, asks
 * the user (the uid is what accepting names).
 */
export function useCalibratePlan(
    org: string,
    assetUid: string,
    available: boolean,
    onFinished: (output: CalibrateOutput, jobUid: string) => void | Promise<void>,
): CalibratePlan {
    const queryClient = useQueryClient()
    const [jobUid, setJobUid] = useState<string | null>(null)
    const [startedAt, setStartedAt] = useState<number | null>(null)
    const [notice, setNotice] = useState<CalibrateNotice | null>(null)
    const settledRef = useRef<string | null>(null)

    const enqueue = $api.useMutation('post', CALIBRATE_PATH)

    // A run started before this mount is adopted, exactly as digitization does:
    // otherwise the editor would offer to start a second one.
    const inFlightQuery = $api.useQuery(
        'get',
        JOBS_PATH,
        {
            params: {
                query: {
                    type: 'floorplan_calibrate',
                    target_entity_type: 'asset',
                    target_entity_uid: assetUid,
                    status: 'running',
                    limit: 1,
                },
                path: { orgCode: org },
            },
        } as never,
        { enabled: !!assetUid && available },
    )

    useEffect(() => {
        if (jobUid) return
        const jobs = (inFlightQuery.data as { jobs?: JobLike[] } | undefined)?.jobs ?? []
        const found = jobs[0]
        if (found?.uid && settledRef.current !== found.uid) {
            setJobUid(found.uid)
            setStartedAt(Date.now())
        }
    }, [inFlightQuery.data, jobUid])

    const poll = useJobPoll(org, jobUid)
    const live = useScopeLive({ entity: 'assets', uid: assetUid })
    const bump = poll.bump
    useEffect(() => {
        if (live) bump()
    }, [live, bump])

    const elapsedSeconds = useElapsedSeconds(startedAt)
    const job = poll.job as JobLike | undefined
    const finalStatus = poll.finalStatus

    useEffect(() => {
        if (!jobUid || !finalStatus) return
        if (settledRef.current === jobUid) return
        settledRef.current = jobUid
        const execution = job?.executions?.[job.executions.length - 1]
        if (finalStatus === 'success') {
            void onFinished((execution?.output ?? {}) as CalibrateOutput, jobUid)
        } else {
            // The job's message already says what it found ("1 dimension
            // found, 2 needed"), so it is printed as-is rather than replaced
            // by a generic failure.
            setNotice({ error: true, message: calibrationErrorOf(execution?.error) })
        }
        setJobUid(null)
        setStartedAt(null)
        void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[1] === JOBS_PATH })
    }, [finalStatus, jobUid, job, onFinished, queryClient])

    const start = useCallback(async (opts?: { representationUid?: string }) => {
        setNotice(null)
        try {
            const res = await enqueue.mutateAsync({
                params: { path: { orgCode: org, assetUid } },
                body: opts?.representationUid ? { representation_uid: opts.representationUid } : {},
            } as never) as { job_uid?: string }
            if (res?.job_uid) {
                settledRef.current = null
                setJobUid(res.job_uid)
                setStartedAt(Date.now())
            }
        } catch (err) {
            setNotice({ error: true, message: apiErrorMessage(err) })
        }
    }, [enqueue, org, assetUid])

    return useMemo(() => ({
        available,
        running: !!jobUid,
        jobUid,
        elapsedSeconds,
        notice,
        enqueueing: enqueue.isPending,
        start,
        dismiss: () => setNotice(null),
        setNotice,
    }), [available, jobUid, elapsedSeconds, notice, enqueue.isPending, start])
}

/**
 * The job's error, stripped of the `CALIBRATION_INCONCLUSIVE:` prefix the
 * backend uses to classify it — the reader wants "5 dimensions disagree by
 * 31 %", not the code in front of it.
 */
function calibrationErrorOf(raw: unknown): string {
    let text: string
    if (!raw) text = 'unknown error'
    else if (typeof raw === 'string') text = raw
    else {
        const obj = raw as { message?: string; error?: string }
        text = obj.message ?? obj.error ?? JSON.stringify(raw)
    }
    return text.replace(/^\s*[A-Z_]+:\s*/, '')
}
